import type { ReactNode } from "react";
import type { Metier } from "models";
import type { FieldPath, FieldValues } from "react-hook-form";
import { useController } from "react-hook-form";
import type { ControlledInput } from "utils";
import { FormControl, FormHelperText } from "@mui/material";
import { MetierAutoComplete } from "./MetierAutoComplete";

interface ControlledMetierAutoCompleteProps<
  T extends FieldValues,
  Name extends FieldPath<T>,
> extends ControlledInput<T, Name> {
  label?: string;
  metiers: Array<Metier>;
  disabled?: boolean;
  defaultHelperText?: string;
  groupesParNiveau?: boolean;
}

export function ControlledMetierAutoComplete<T extends FieldValues, Name extends FieldPath<T>>({
  name,
  control,
  label,
  defaultValue,
  rules,
  metiers,
  disabled = false,
  defaultHelperText,
  groupesParNiveau = false,
}: Readonly<ControlledMetierAutoCompleteProps<T, Name>>): ReactNode {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control, rules, defaultValue });

  return (
    <FormControl fullWidth disabled={disabled}>
      <MetierAutoComplete
        label={label}
        value={value}
        onChange={onChange}
        metiers={metiers}
        groupesParNiveau={groupesParNiveau}
      />
      {(error?.message ?? defaultHelperText) != null && (
        <FormHelperText error={error != null}>{error?.message ?? defaultHelperText}</FormHelperText>
      )}
    </FormControl>
  );
}
